<!--
  Picks images and uploads them to outstand immediately (not on form submit),
  so the parent form already holds media entries by the time it posts.

  `media` is bindable and holds what the server wants: [{ id, url, filename, expires_at }].
  `uploading` is bindable too, so the parent can disable its submit button
  while files are still in flight.

  Entries in flight are tracked separately in `items`, keyed by a local counter,
  since they have no id until the upload resolves.
-->
<script>
  import Icon from '@iconify/svelte';

  let { media = $bindable([]), uploading = $bindable(0), disabled = false } = $props();

  // { key, id, previewUrl, uploading, error }. previewUrl is a local object
  // URL for the thumbnail before (and after) the upload resolves.
  let items = $state(
    media.map((m, i) => ({ key: -(i + 1), id: m.id, previewUrl: m.url, uploading: false, error: null })),
  );
  let counter = 0;

  $effect(() => {
    uploading = items.filter((i) => i.uploading).length;
  });

  let failed = $derived(items.filter((i) => i.error));

  const MAX_BYTES = 15 * 1024 * 1024;
  const ACCEPTED = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  function csrfToken() {
    const match = document.cookie.match(/(?:^|; )XSRF-TOKEN=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function patch(key, changes) {
    items = items.map((i) => (i.key === key ? { ...i, ...changes } : i));
  }

  // Reject locally what the server would reject anyway, so the user gets the
  // message immediately instead of after pushing 15MB over a phone connection.
  // Type is only advisory here (the browser's guess); the server re-checks
  // against magic bytes.
  function localError(file) {
    if (file.size === 0) return 'That file is empty.';
    if (file.size > MAX_BYTES) return 'Too large (max 15MB).';
    if (file.type && !ACCEPTED.includes(file.type)) return 'Use a JPEG, PNG, GIF, or WebP.';
    return null;
  }

  async function send(file) {
    const body = new FormData();
    body.append('file', file);

    const response = await fetch('/media_uploads', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      body,
      credentials: 'same-origin',
    });

    // A 419/401 means the CSRF token or session went stale while the page sat
    // open. A non-JSON body means we hit an error page rather than the API.
    let data = null;
    try {
      data = await response.json();
    } catch {
      throw new Error(
        response.status === 0 ? 'Network error. Check your connection.' : `Upload failed (${response.status}).`,
      );
    }
    if (!response.ok) throw new Error(data?.error || `Upload failed (${response.status}).`);
    if (!data?.id) throw new Error('Upload failed: no media id returned.');
    return data;
  }

  async function upload(file, existingKey = null) {
    const clientError = localError(file);
    const key = existingKey ?? ++counter;

    if (existingKey === null) {
      items = [
        ...items,
        {
          key,
          id: null,
          file,
          previewUrl: URL.createObjectURL(file),
          uploading: !clientError,
          error: clientError,
        },
      ];
      if (clientError) return;
    } else {
      patch(key, { uploading: true, error: null });
    }

    try {
      const data = await send(file);
      patch(key, { id: data.id, uploading: false, error: null });
      // Re-read `media` here rather than closing over it: several uploads can
      // be in flight and each must append to the latest list, not a snapshot.
      media = [...media, { id: data.id, url: data.url, filename: data.filename, expires_at: data.expires_at }];
    } catch (err) {
      patch(key, { uploading: false, error: err.message || 'Upload failed.' });
    }
  }

  function retry(entry) {
    if (entry.file) upload(entry.file, entry.key);
  }

  function pick(e) {
    Array.from(e.target.files || []).forEach((f) => upload(f));
    e.target.value = '';
  }

  function remove(entry) {
    items = items.filter((i) => i.key !== entry.key);
    if (entry.id) media = media.filter((m) => m.id !== entry.id);
    if (entry.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(entry.previewUrl);
  }
</script>

<div class="flex flex-wrap gap-3">
  {#each items as entry (entry.key)}
    <div class="relative w-20 h-20 rounded-lg overflow-hidden border border-secondary-200">
      {#if entry.previewUrl}
        <img src={entry.previewUrl} alt="" class="w-full h-full object-cover" />
      {:else}
        <div class="w-full h-full flex items-center justify-center bg-secondary-50 text-secondary-300">
          <Icon icon="mdi:image-outline" class="text-xl" />
        </div>
      {/if}
      {#if entry.uploading}
        <div class="absolute inset-0 bg-black/40 flex items-center justify-center">
          <Icon icon="mdi:loading" class="animate-spin text-white text-xl" />
        </div>
      {/if}
      {#if entry.error}
        <div class="absolute inset-0 bg-red-900/70 flex items-center justify-center" title={entry.error}>
          {#if entry.file}
            <button
              type="button"
              onclick={() => retry(entry)}
              class="flex flex-col items-center text-white"
              aria-label="Retry upload: {entry.error}"
            >
              <Icon icon="mdi:refresh" class="text-xl" />
              <span class="text-[10px] leading-tight">Retry</span>
            </button>
          {:else}
            <Icon icon="mdi:alert-circle-outline" class="text-white text-xl" />
          {/if}
        </div>
      {/if}
      <button
        type="button"
        onclick={() => remove(entry)}
        class="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 rounded-full p-0.5"
        aria-label="Remove image"
      >
        <Icon icon="mdi:close" class="text-white text-sm" />
      </button>
    </div>
  {/each}
  {#if !disabled}
    <label class="w-20 h-20 rounded-lg border-2 border-dashed border-secondary-300 flex items-center justify-center cursor-pointer hover:bg-secondary-50 text-secondary-400">
      <Icon icon="mdi:image-plus" class="text-2xl" />
      <input type="file" accept={ACCEPTED.join(',')} multiple class="sr-only" onchange={pick} />
    </label>
  {/if}
</div>

{#if failed.length > 0}
  <ul class="mt-2 space-y-1 text-sm text-red-600" aria-live="polite">
    {#each failed as entry (entry.key)}
      <li>{entry.file?.name ? `${entry.file.name}: ` : ''}{entry.error}</li>
    {/each}
  </ul>
{/if}
